import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  PUBLIC_LOCALES,
  PUBLIC_LOCALE_LABELS,
  PUBLIC_LOCALE_STORAGE_KEY,
  PUBLIC_MESSAGES,
} from "./publicI18n/messages";

const PublicI18nContext = createContext(null);

export const PUBLIC_LOCALE_FORMATS = {
  en: "en-US",
  es: "es-US",
};

const getByPath = (obj, path) =>
  String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((value, segment) => (value && value[segment] !== undefined ? value[segment] : undefined), obj);

const interpolate = (template, vars = {}) =>
  String(template).replace(/\{(\w+)\}/g, (_, key) => {
    const value = vars[key];
    return value === null || value === undefined ? "" : String(value);
  });

export const getPublicMessage = (locale, key, vars) => {
  const message =
    getByPath(PUBLIC_MESSAGES[locale], key) ??
    getByPath(PUBLIC_MESSAGES.en, key);

  if (message === undefined) {
    return key;
  }

  return typeof message === "string" ? interpolate(message, vars) : message;
};

const getInitialLocale = () => {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem(PUBLIC_LOCALE_STORAGE_KEY);
  return PUBLIC_LOCALES.includes(stored) ? stored : "en";
};

export function PublicI18nProvider({ children }) {
  const [locale, setLocaleState] = useState(getInitialLocale);

  const setLocale = (nextLocale) => {
    setLocaleState(PUBLIC_LOCALES.includes(nextLocale) ? nextLocale : "en");
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(PUBLIC_LOCALE_STORAGE_KEY, locale);
    }
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      t: (key, vars) => getPublicMessage(locale, key, vars),
      localeLabel: PUBLIC_LOCALE_LABELS[locale] || PUBLIC_LOCALE_LABELS.en,
    }),
    [locale]
  );

  return <PublicI18nContext.Provider value={value}>{children}</PublicI18nContext.Provider>;
}

export function usePublicI18n() {
  const context = useContext(PublicI18nContext);
  if (!context) {
    throw new Error("usePublicI18n must be used within a PublicI18nProvider");
  }
  return context;
}

// For components rendered in both public and staff layouts: returns null
// instead of throwing when no provider is mounted.
export function usePublicI18nOptional() {
  return useContext(PublicI18nContext);
}
